// web/src/shell/SideNav.tsx — the persistent left navigation (M0.4). Brand mark
// at the top, the primary IA below (from nav.ts), and a footer with the
// collapse toggle. Keyboard-navigable (real <NavLink>s, visible focus
// ring from global.css), active-route aware, and collapsible on narrow screens.
// Token-only styling lives in shell.css.
import React from 'react';
import { NavLink } from 'react-router-dom';
import { Hexagon, PanelLeftClose, PanelLeft } from 'lucide-react';
import { Icon } from '../components/ui/Icon';
import { NAV_ITEMS } from './nav';
import { useShell } from './ShellContext';

export interface SideNavProps {
  collapsed: boolean;
  onToggle: () => void;
}

export function SideNav({ collapsed, onToggle }: SideNavProps): React.JSX.Element {
  const { wsConnected } = useShell();
  return (
    <nav
      className={`bgNav ${collapsed ? 'bgNavCollapsed' : ''}`}
      aria-label="Primary"
    >
      <div className="bgNavBrand">
        <span className="bgGlassMark" aria-hidden="true">
          <Icon icon={Hexagon} size="md" />
        </span>
        {!collapsed && (
          <span className="bgBrandText">
            <span className="bgBrandMark">Break Glass</span>
            <span className="bgBrandKicker">Emergency access</span>
          </span>
        )}
      </div>

      <ul className="bgNavList">
        {NAV_ITEMS.map((item) => (
          <li key={item.path}>
            <NavLink
              to={item.path}
              end={item.path === '/'}
              className={({ isActive }) => `bgNavLink ${isActive ? 'bgNavLinkActive' : ''}`}
              title={collapsed ? item.label : item.blurb}
            >
              <span className="bgNavBar" aria-hidden="true" />
              <Icon icon={item.icon} size="md" />
              {!collapsed && <span className="bgNavLabel">{item.label}</span>}
            </NavLink>
          </li>
        ))}
      </ul>

      <div className="bgNavStatus" role="status" aria-label="System status">
        <span
          className={`bgNavStatusDot ${wsConnected ? 'bgNavStatusArmed' : 'bgNavStatusReconnect'}`}
          aria-hidden="true"
        />
        {!collapsed && (
          <span className="bgNavStatusText">
            {wsConnected ? 'System armed' : 'Reconnecting…'}
          </span>
        )}
      </div>

      <div className="bgNavFoot">
        <button
          type="button"
          className="bgNavLink bgNavCollapseBtn"
          onClick={onToggle}
          aria-pressed={collapsed}
          aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
        >
          <Icon icon={collapsed ? PanelLeft : PanelLeftClose} size="md" />
          {!collapsed && <span className="bgNavLabel">Collapse</span>}
        </button>
      </div>
    </nav>
  );
}

export default SideNav;
